/**
 * R69d · useApolloBulkEnrich — shared hook for the Search + Prospects
 * screens' bulk-enrich flows. Centralises the request shape, toast
 * copy, and loading state so both call sites stay in lockstep.
 *
 * R69e · Now chunks large runs (>10 items) client-side so the UI can
 * display a live progress bar (X of N done). Backend still batches
 * to Apollo bulk_match internally, but we split at the client so a
 * 200-row enrichment shows meaningful "3/20 batches done" progress
 * instead of a single 5-minute spinner.
 *
 * Usage:
 *   const { run, loading, progress } = useApolloBulkEnrich(wsId);
 *   await run(items, { revealPhone: true });
 *   // progress = {current, total, enriched, not_found, errors, emails, phones}
 */
import { useCallback, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const CLIENT_CHUNK = 10;  // matches Apollo's per-request cap

export default function useApolloBulkEnrich(wsId, opts = {}) {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const chunkSize = Math.max(1, Math.min(50, opts.chunkSize || CLIENT_CHUNK));

  const run = useCallback(async (items, opts = {}) => {
    const { revealEmail = true, revealPhone = true, silent = false } = opts;
    if (!wsId) return null;
    if (!Array.isArray(items) || items.length === 0) {
      if (!silent) toast.error("Nothing to enrich");
      return null;
    }
    setLoading(true);
    const rollup = {
      current: 0, total: items.length,
      enriched: 0, not_found: 0, errors: 0,
      emails_revealed: 0, phones_revealed: 0,
      refreshed_ids: 0,   // R69h · stale-id refresh count
    };
    setProgress({ ...rollup });
    const allResults = [];
    try {
      // Split into 10-per-request chunks so we can update progress after
      // each chunk instead of after the whole run.
      for (let i = 0; i < items.length; i += chunkSize) {
        const chunk = items.slice(i, i + chunkSize);
        try {
          const { data } = await api.post(`/workspaces/${wsId}/apollo/enrich/people-bulk`, {
            items: chunk,
            reveal_personal_emails: revealEmail,
            reveal_phone_number: revealPhone,
          });
          const s = data?.stats || {};
          rollup.enriched += s.enriched || 0;
          rollup.not_found += s.not_found || 0;
          rollup.errors += s.errors || 0;
          rollup.emails_revealed += s.emails_revealed || 0;
          rollup.phones_revealed += s.phones_revealed || 0;
          rollup.refreshed_ids += s.refreshed_ids || 0;
          if (Array.isArray(data?.results)) allResults.push(...data.results);
        } catch (e) {
          // Failing one chunk shouldn't kill the whole run — mark items
          // as errors and keep going.
          rollup.errors += chunk.length;
          chunk.forEach(() => allResults.push({ status: "error", message: apiErrorMessage(e, "Chunk failed") }));
        }
        rollup.current = Math.min(rollup.current + chunk.length, items.length);
        setProgress({ ...rollup });
      }
      if (!silent) {
        toast.success(
          `Enriched ${rollup.enriched}/${rollup.total} · ${rollup.emails_revealed} emails · ${rollup.phones_revealed} phones` +
          (rollup.not_found ? ` · ${rollup.not_found} not found` : "") +
          (rollup.errors ? ` · ${rollup.errors} errors` : "")
        );
        // R69h · Diagnostic hint when some prospects were only found
        // via the name-fallback retry. Their apollo_id has just been
        // refreshed — future enrichments hit the fast-path.
        if (rollup.refreshed_ids > 0) {
          toast.info(
            `Refreshed ${rollup.refreshed_ids} stale Apollo ID${rollup.refreshed_ids === 1 ? "" : "s"} — those prospects were found by name-match. Next enrich will be faster.`,
            { duration: 6000 }
          );
        }
      }
      return { stats: rollup, results: allResults };
    } finally {
      setLoading(false);
      // Keep the final progress visible for a beat so the bar reaches 100%
      setTimeout(() => setProgress(null), 1500);
    }
  }, [wsId, chunkSize]);

  return { run, loading, progress };
}
